import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Box,
  ClipboardCheck,
  Building2,
  Braces,
  ShieldAlert,
  Users,
  Shield,
  ScrollText,
  Settings,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { useTranslation } from "@/hooks/useTranslation";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="px-2 pt-4 pb-1"
      style={{
        fontSize: "11.5px",
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        letterSpacing: "1.5px",
        color: "var(--text-sidebar-muted, var(--text-muted))",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function SidebarNavItem({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.href}
      end={item.href === "/dashboard"}
      className={({ isActive }) =>
        cn(
          // Base item styles — design-system.md §5 Nav Item States
          "flex items-center gap-3 px-3 py-2 rounded-control",
          "text-nav-item font-medium transition-colors duration-150",
          "cursor-pointer select-none sidebar-nav-item",
          isActive
            ? // Active: sidebar-specific accent fill so it works on dark navy in both themes
              "sidebar-nav-item--active"
            : // Default: sidebar text, sidebar hover bg, sidebar-muted icon
              "sidebar-nav-item--inactive"
        )
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

// ─── Avatar helper ────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Nav items computed inside the component so translations re-render on language change
  const NAV_ITEMS: NavItem[] = [
    { label: t("nav.dashboard"), href: "/dashboard", icon: <LayoutDashboard size={20} /> },
    { label: t("nav.apps"), href: "/apps", icon: <Box size={20} /> },
    { label: t("nav.assessments"), href: "/assessments", icon: <ClipboardCheck size={20} /> },
    { label: t("nav.vendors"), href: "/vendors", icon: <Building2 size={20} /> },
    { label: t("nav.schemas"), href: "/schemas", icon: <Braces size={20} /> },
    { label: t("nav.riskReport"), href: "/risks", icon: <ShieldAlert size={20} /> },
  ];

  const ADMIN_ITEMS: NavItem[] = [
    { label: t("nav.users"), href: "/users", icon: <Users size={20} /> },
    { label: t("nav.roles"), href: "/roles", icon: <Shield size={20} /> },
    { label: t("nav.auditLogs"), href: "/audit-logs", icon: <ScrollText size={20} /> },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      style={{
        width: "256px",
        minWidth: "256px",
        backgroundColor: "var(--bg-sidebar)",
        borderRight: "1px solid var(--border-subtle)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: "8px",
        gap: "16px",
        position: "sticky",
        top: 0,
        overflow: "hidden",
      }}
    >
      {/* ── Sidebar Header ─────────────────────────────────────── */}
      <div className="flex items-center px-2 py-2">
        <span
          style={{
            fontSize: "24px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 500,
            letterSpacing: "3.5px",
            color: "var(--text-sidebar-active, var(--text-primary))",
          }}
        >
          KIBORG
        </span>
      </div>

      {/* ── Sidebar Content ────────────────────────────────────── */}
      <nav className="flex-1 flex flex-col gap-nav overflow-y-auto">
        <SectionTitle>{t("nav.navigation")}</SectionTitle>
        {NAV_ITEMS.map((item) => (
          <SidebarNavItem key={item.href} item={item} />
        ))}

        <SectionTitle>{t("nav.adminPanel")}</SectionTitle>
        {ADMIN_ITEMS.map((item) => (
          <SidebarNavItem key={item.href} item={item} />
        ))}
      </nav>

      {/* ── Sidebar Footer ─────────────────────────────────────── */}
      <div
        className="border-t"
        style={{ borderColor: "var(--border-sidebar, var(--border-subtle))", paddingTop: "8px" }}
      >
        {/* Settings link */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2 rounded-control mb-1",
              "text-nav-item font-medium transition-colors duration-150 cursor-pointer sidebar-nav-item",
              isActive
                ? "sidebar-nav-item--active"
                : "sidebar-nav-item--inactive"
            )
          }
        >
          <Settings size={20} />
          <span>{t("nav.settings")}</span>
        </NavLink>

        {/* User info row */}
        <div className="flex items-center justify-between gap-2 px-2 py-2 rounded-control cursor-pointer transition-colors group sidebar-user-row">
          <div className="flex items-center gap-inner min-w-0">
            {/* Avatar */}
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                width: "32px",
                height: "32px",
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "var(--text-sidebar, #d9d9d9)",
                fontSize: "13px",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              {user?.name ? getInitials(user.name) : "?"}
            </div>
            {/* Name + email */}
            <div className="min-w-0">
              <p
                className="truncate"
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "var(--text-sidebar-active, var(--text-sidebar))",
                  lineHeight: 1.3,
                }}
              >
                {user?.name ?? "User"}
              </p>
              <p
                className="truncate"
                style={{
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "var(--text-sidebar-muted, var(--text-muted))",
                  lineHeight: 1.3,
                }}
              >
                {user?.email ?? ""}
              </p>
            </div>
          </div>
          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-opacity p-1 rounded sidebar-logout-btn"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={14} style={{ color: "var(--text-sidebar-muted, var(--text-muted))" }} />
          </button>
        </div>
      </div>
    </aside>
  );
}
